import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Modal } from "../../components/ui-modal";
import FormCard from "../../components/FormCard";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const QUESTION_TYPES = [
  ["reponse_courte", "Réponse courte"],
  ["reponse_longue", "Réponse longue"],
  ["choix_unique", "Choix unique"],
  ["cases_a_cocher", "Cases à cocher"],
  ["email", "E-mail"],
  ["telephone", "Téléphone"],
  ["lien", "Lien / URL"],
];
const CHOICE_TYPES = ["choix_unique", "cases_a_cocher"];

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`);
const blankQuestion = () => ({ id: uid(), type: "reponse_courte", label: "", description: "", required: false, options: [] });
const blankSection = () => ({ id: uid(), title: "", description: "", questions: [] });

export default function FormTemplateEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "new";
  const [name, setName] = useState("");
  const [sections, setSections] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragSection, setDragSection] = useState(null);
  const [dragQuestion, setDragQuestion] = useState(null); // { sectionId, index }
  // Snapshot du template tel que chargé depuis le serveur — jamais muté par
  // les fonctions d'édition du brouillon (updateSection/updateQuestion...),
  // qui n'écrivent que dans `sections`. Sert de source pour l'aperçu, pour ne
  // jamais prévisualiser des changements non enregistrés.
  const [savedTemplate, setSavedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [intro, setIntro] = useState({ title: "", description: "", button_label: "" });
  const [outro, setOutro] = useState({ message: "" });

  useEffect(() => {
    if (isNew) { setSections([blankSection()]); setLoaded(true); return; }
    api.get("/admin/form-templates").then((r) => {
      const t = r.data.find((x) => x.id === id);
      if (!t) { toast.error("Modèle introuvable"); nav("/admin/automations-formulaires"); return; }
      setName(t.name);
      setSections(t.sections.map((s) => ({ ...s, questions: s.questions.map((q) => ({ ...q, options: [...(q.options || [])] })) })));
      setIntro({ title: t.intro?.title || "", description: t.intro?.description || "", button_label: t.intro?.button_label || "" });
      setOutro({ message: t.outro?.message || "" });
      setSavedTemplate(t);
      setLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!loaded) return null;

  const findSection = (sid) => sections.find((s) => s.id === sid);
  const updateSection = (sid, patch) => setSections(sections.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
  const addSection = () => setSections([...sections, blankSection()]);
  const removeSection = (sid) => setSections(sections.filter((s) => s.id !== sid));
  const onDropSection = (i) => {
    if (dragSection === null || dragSection === i) { setDragSection(null); return; }
    const a = [...sections];
    const [it] = a.splice(dragSection, 1);
    a.splice(i, 0, it);
    setSections(a);
    setDragSection(null);
  };

  const addQuestion = (sid) => updateSection(sid, { questions: [...findSection(sid).questions, blankQuestion()] });
  const removeQuestion = (sid, qid) => updateSection(sid, { questions: findSection(sid).questions.filter((q) => q.id !== qid) });
  const updateQuestion = (sid, qid, patch) => updateSection(sid, {
    questions: findSection(sid).questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)),
  });
  const onDropQuestion = (sid, i) => {
    if (!dragQuestion || dragQuestion.sectionId !== sid || dragQuestion.index === i) { setDragQuestion(null); return; }
    const qs = [...findSection(sid).questions];
    const [it] = qs.splice(dragQuestion.index, 1);
    qs.splice(i, 0, it);
    updateSection(sid, { questions: qs });
    setDragQuestion(null);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Nom requis");
    if (sections.length === 0) return toast.error("Ajoutez au moins une section");
    if (!intro.title.trim()) return toast.error("Le titre de l'écran d'accueil est obligatoire");
    if (!intro.description.trim()) return toast.error("Le message de l'écran d'accueil est obligatoire");
    if (!outro.message.trim()) return toast.error("Le message de l'écran de fin est obligatoire");
    for (const s of sections) {
      if (!s.title.trim()) return toast.error("Chaque section doit avoir un titre");
      for (const q of s.questions) {
        if (!q.label.trim()) return toast.error("Chaque question doit avoir un intitulé");
        if (CHOICE_TYPES.includes(q.type) && (q.options || []).filter((o) => o.trim()).length === 0) {
          return toast.error(`Ajoutez au moins une option pour « ${q.label} »`);
        }
      }
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        sections,
        intro: { title: intro.title.trim(), description: intro.description.trim(), button_label: intro.button_label.trim() },
        outro: { message: outro.message.trim() },
      };
      if (isNew) await api.post("/admin/form-templates", body);
      else await api.put(`/admin/form-templates/${id}`, body);
      toast.success("Modèle enregistré");
      nav("/admin/automations-formulaires");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageTitle overline="Automatisations" title={isNew ? "Nouveau modèle de formulaire" : "Modifier le modèle"}
        action={<div className="flex gap-2">
          <button type="button" onClick={() => nav("/admin/automations-formulaires")} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
          {!isNew && savedTemplate && (
            <button type="button" onClick={() => setPreviewOpen(true)} data-testid="form-template-preview"
              className="border border-marine/25 text-marine rounded-sm px-4 py-2 text-sm hover:bg-marine hover:text-white transition-colors flex items-center gap-1.5">
              <Icons.Eye size={16} /> Prévisualiser
            </button>
          )}
          <button type="button" onClick={save} disabled={saving} data-testid="form-template-save"
            className="bg-marine text-white rounded-sm px-5 py-2 text-sm disabled:opacity-50">Enregistrer</button>
        </div>} />

      <div className="max-w-3xl">
        <div className="mb-5">
          <label htmlFor="tpl-name" className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Nom du modèle</label>
          <input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="form-template-name" className="inp w-full" />
        </div>

        <div className="mb-5 border border-marine/15 rounded-sm p-4 bg-sable">
          <div className="text-sm font-semibold text-marine mb-3">Écran d'accueil</div>
          <div className="space-y-3">
            <div>
              <label htmlFor="form-intro-title" className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Titre d'accueil</label>
              <input id="form-intro-title" value={intro.title} onChange={(e) => setIntro({ ...intro, title: e.target.value })}
                data-testid="form-intro-title" className="inp w-full" />
            </div>
            <div>
              <label htmlFor="form-intro-description" className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Description</label>
              <input id="form-intro-description" value={intro.description} onChange={(e) => setIntro({ ...intro, description: e.target.value })}
                data-testid="form-intro-description" className="inp w-full" />
            </div>
            <div>
              <label htmlFor="form-intro-button" className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Libellé du bouton</label>
              <input id="form-intro-button" value={intro.button_label} onChange={(e) => setIntro({ ...intro, button_label: e.target.value })}
                data-testid="form-intro-button" placeholder="Commencer" className="inp w-full" />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {sections.map((s, si) => (
            <div key={s.id} draggable onDragStart={() => setDragSection(si)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropSection(si)}
              data-testid={`section-${s.id}`} className="border border-marine/15 rounded-sm p-4 bg-sable">
              <div className="flex items-start gap-2 mb-3">
                <span className="mt-2 cursor-grab shrink-0" aria-hidden="true"><Icons.GripVertical size={16} className="text-muet" /></span>
                <div className="flex-1 space-y-2">
                  <div>
                    <label htmlFor={`section-title-${s.id}`} className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1">Titre de la section</label>
                    <input id={`section-title-${s.id}`} value={s.title} onChange={(e) => updateSection(s.id, { title: e.target.value })}
                      data-testid={`section-title-input-${s.id}`} className="inp w-full" />
                  </div>
                  <div>
                    <label htmlFor={`section-desc-${s.id}`} className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1">Description (optionnel)</label>
                    <input id={`section-desc-${s.id}`} value={s.description} onChange={(e) => updateSection(s.id, { description: e.target.value })}
                      data-testid={`section-desc-input-${s.id}`} className="inp w-full" />
                  </div>
                </div>
                <button type="button" onClick={() => removeSection(s.id)} data-testid={`section-remove-${s.id}`}
                  aria-label={`Supprimer la section ${s.title || si + 1}`} className="text-rouge hover:bg-rouge/10 rounded-sm p-1.5 shrink-0">
                  <Icons.Trash2 size={16} />
                </button>
              </div>

              <div className="space-y-2 pl-6">
                {s.questions.map((q, qi) => (
                  <div key={q.id} draggable onDragStart={() => setDragQuestion({ sectionId: s.id, index: qi })}
                    onDragOver={(e) => e.preventDefault()} onDrop={() => onDropQuestion(s.id, qi)}
                    data-testid={`question-${q.id}`} className="border border-marine/10 rounded-sm p-3 bg-creme">
                    <div className="flex items-start gap-2">
                      <span className="mt-2 cursor-grab shrink-0" aria-hidden="true"><Icons.GripVertical size={16} className="text-muet" /></span>
                      <div className="flex-1 space-y-2">
                        <div className="grid sm:grid-cols-2 gap-2">
                          <div>
                            <label htmlFor={`q-label-${q.id}`} className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1">Intitulé de la question</label>
                            <input id={`q-label-${q.id}`} value={q.label} onChange={(e) => updateQuestion(s.id, q.id, { label: e.target.value })}
                              data-testid={`question-label-${q.id}`} className="inp w-full text-sm" />
                          </div>
                          <div>
                            <label htmlFor={`q-type-${q.id}`} className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1">Type de champ</label>
                            <select id={`q-type-${q.id}`} value={q.type}
                              onChange={(e) => updateQuestion(s.id, q.id, {
                                type: e.target.value,
                                options: CHOICE_TYPES.includes(e.target.value) ? (q.options?.length ? q.options : [""]) : [],
                              })}
                              data-testid={`question-type-${q.id}`} className="inp w-full text-sm">
                              {QUESTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                          </div>
                        </div>
                        {CHOICE_TYPES.includes(q.type) && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1">Options</div>
                            <div className="space-y-1.5">
                              {(q.options || []).map((opt, oi) => (
                                <div key={oi} className="flex gap-1.5">
                                  <label htmlFor={`q-opt-${q.id}-${oi}`} className="sr-only">Option {oi + 1} de « {q.label || "cette question"} »</label>
                                  <input id={`q-opt-${q.id}-${oi}`} value={opt}
                                    onChange={(e) => updateQuestion(s.id, q.id, { options: q.options.map((o, x) => (x === oi ? e.target.value : o)) })}
                                    data-testid={`question-option-${q.id}-${oi}`} className="inp flex-1 text-sm" />
                                  <button type="button" onClick={() => updateQuestion(s.id, q.id, { options: q.options.filter((_, x) => x !== oi) })}
                                    aria-label={`Supprimer l'option ${oi + 1}`} className="text-rouge px-2"><Icons.X size={16} /></button>
                                </div>
                              ))}
                              <button type="button" onClick={() => updateQuestion(s.id, q.id, { options: [...(q.options || []), ""] })}
                                data-testid={`question-option-add-${q.id}`} className="text-xs text-marine hover:underline">+ Ajouter une option</button>
                            </div>
                          </div>
                        )}
                        <label className="flex items-center gap-2 text-xs text-marine/80 cursor-pointer">
                          <input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(s.id, q.id, { required: e.target.checked })}
                            data-testid={`question-required-${q.id}`} /> Réponse obligatoire
                        </label>
                      </div>
                      <button type="button" onClick={() => removeQuestion(s.id, q.id)} data-testid={`question-remove-${q.id}`}
                        aria-label={`Supprimer la question ${q.label || qi + 1}`} className="text-rouge hover:bg-rouge/10 rounded-sm p-1.5 shrink-0">
                        <Icons.Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => addQuestion(s.id)} data-testid={`question-add-${s.id}`}
                  className="text-xs border border-marine/25 text-marine rounded-sm px-2.5 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1">
                  <Icons.Plus size={16} /> Ajouter une question
                </button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={addSection} data-testid="section-add"
          className="mt-4 text-sm border border-bordeaux/30 text-bordeaux rounded-sm px-3 py-2 hover:bg-bordeaux hover:text-white transition-colors flex items-center gap-1.5">
          <Icons.Plus size={16} /> Ajouter une section
        </button>

        <div className="mt-5 border border-marine/15 rounded-sm p-4 bg-sable">
          <div className="text-sm font-semibold text-marine mb-3">Écran de fin</div>
          <div>
            <label htmlFor="form-outro-message" className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Message de fin</label>
            <input id="form-outro-message" value={outro.message} onChange={(e) => setOutro({ message: e.target.value })}
              data-testid="form-outro-message" className="inp w-full" />
          </div>
        </div>
      </div>

      {previewOpen && savedTemplate && (
        <Modal onClose={() => setPreviewOpen(false)} title="Aperçu du formulaire" wide>
          {/* Objet minimal construit à partir du template SAUVEGARDÉ, pas du
              brouillon en cours — status/id/name du template omis exprès :
              son `status` (publication "active"/"archived") n'a pas le même
              sens que le `status` de remplissage que FormCard sait lire. */}
          <FormCard form={{ sections: savedTemplate.sections, intro: savedTemplate.intro, outro: savedTemplate.outro }} previewMode testId="template-preview" />
        </Modal>
      )}
    </div>
  );
}
